import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import type { RxEventPayloadBase } from './rxEvent.types';

export class RxEventHub<RxEventPayloadMap extends Record<string, RxEventPayloadBase>> {
  private subject: Subject<RxEventPayloadMap[keyof RxEventPayloadMap]>;

  constructor() {
    this.subject = new Subject();
  }

  /**
   * Broadcast an event across the app.
   */
  broadcast<EventName extends keyof RxEventPayloadMap, Data extends RxEventPayloadMap[EventName]['data']>(
    name: EventName,
    data: Data,
  ): void {
    // It's complaining about a very specific edge case, which is a non-issue with this amount of strict typing.
    this.subject.next({ name, data } as RxEventPayloadMap[keyof RxEventPayloadMap]);
  }

  /**
   * Subscribe to the specified `eventName` and trigger the specified `callback` when it happens.
   * @returns A subscription object, which you can use to `.unsubscribe()`.
   */
  on<EventName extends keyof RxEventPayloadMap, Payload extends RxEventPayloadMap[EventName]>(
    eventName: EventName,
    handler: (payload: Payload) => void,
  ) {
    return (
      this.subject
        .pipe(filter((payload) => payload.name === eventName))
        // It's complaining about a very specific edge case, which is a non-issue with this amount of strict typing.
        .subscribe((payload) => handler(payload as Payload))
    );
  }
}
